import { useState, useMemo, useCallback, useEffect } from "react";
import { Button } from "@aidron/aidron-ds";

import { usePurchaseRecommendation } from "../model/usePurchaseRecommendation";
import { useUserCtx } from '../model/useUsersCtx';
import { useOrders } from "../model/useOrders";

import { useStoreInformation } from "./stores/storeInformationStore";

import { 
  EffectivePurchaseCell,
  MinStockCell,
  PackToUnitRatioCell,
  PrefixNameCell,
  CellValue,
  Header, 
  SubHeader,
  LeadTimeCell,
  SuppliersCell,
  ProjectedSalesCell,
  SelectCell,
  IconButtonWithModal
} from "../components/SharedComponents/sharedComponents.index";

import { 
    ALLOWED_PURCHASE_STATUS, 
    COLUMNS_LABELS, 
    COLUMNS_IDENTIFIERS,
    COLUMNS_DESCRIPTIONS,
    USER_ROLES,
    ORDER_STATUS,
    ORDER_ITEM_STATUS,
    ELIGIBLE_ORDERS_STRATEGY_VALUES,
    DEFAULT_VALUES
} from "../components/components.constants";

import { SearchIcon } from "../modules/PurchaseManagementModule/PurchaseHistory/purchaseHistory.index";

import { textFormatter } from "../utils/formatter/formatter";

import { i18n } from "../translate/i18n";
import { useSeetingsViewModel } from "./useSettingsViewModel";
import { InformationForTheUser } from "../components/SharedComponents/TableCells/TableCells";

import { getPurchaseOrderEligibility } from "./strategy/purchaseOrderEligibility/purchaseOrderEligibility.strategy.index"
import { isEligibleForPurchase } from "./strategy/purchaseOrderEligibility/withOriginalValues/withOriginalValues";

export const usePurchaseRecommendationViewModel = ({ 
    shouldFetchPurchaseRecommendationData, 
    shouldFetchOrdersData,
    shouldFetchAvailableItens
}, vmOptions = {}) => {

    const { t } = i18n;

    /** Estados gerenciados pela VM
     * Os estados serão apresentados por blocos separados por comentários de linha única
     */

    // Ocultação/congelamento de colunas
    const [freezeColumns, setFreezeColumns] = useState(true);

    // Estados para modo de edição
    const [inputs, setInputs] = useState([]);
    const [showInputs, setShowInputs] = useState(false);
    const [checkedProducts, setCheckedProducts] = useState([]);

    // Estados para exibição e ocultação de componentes de filtro, e exibição de informações na UI
    const [showColumnFilters, setShowColumnFilters] = useState(false);
    const [appliedFiltersCount, setAppliedFiltersCount] = useState(0);
    const [showFilters, setShowFilters] = useState(false);

    // Estados para exibição de modais e para dados específicos aos modais
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showCancelModalOrderRollback, setShowCancelModalOrderRollback] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showUpdateRecommendationError, setShowUpdateRecommendationError] = useState(false);
    const [updatedProductsList, setUpdatedProductsList] = useState([]);
    const [blockedProductsList, setBlockedProductsList] = useState([]);
    const [openOrderConfirmation, setOpenOrderConfirmation] = useState(false);
    const [selectedSupplierFromFilter, setSelectedSupplierFromFilter] = useState(null);
    const [isPatchingOrderInParent, setIsPatchingOrderInParent] = useState(false);    

    // Estados para manipulação de dados de tabelas, seleção de pedidos 
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [localOrderItems, setLocalOrderItems] = useState([]);
    const [selectedProductIds, setSelectedProductIds] = useState([]);   
    const [showNonEligibleProducts, setShowNonEligibleProducts] = useState(true);    
    const [sorting, setSorting] = useState([{
        id: COLUMNS_IDENTIFIERS.PURCHASE_REC_7,
        desc: true
    }]);

    // Estabilização de setters
    const setAppliedFiltersCountAction = useCallback((val) => setAppliedFiltersCount(val), []);
    const setFreezeColumnsAction = useCallback((val) => setFreezeColumns(val), []);
    const setShowColumnFiltersAction = useCallback((val) => setShowColumnFilters(val), []);
    const setInputsAction = useCallback((val) => setInputs(val), []);        

    /** Models
     * A fonte de dados de models consumidos pela VM; incluso o consumo
     * de dados via stores (zustand)
     */

    const { selectedStoreId } = useStoreInformation();

    const vmSettings = useSeetingsViewModel({ shouldFetchUserCtx: true, shouldFetchStores: true });

    const { supplierId } = vmOptions || {};

    const {
        data: apiData,
        isLoading,
        updateRecommendation,
        updateError,
        isUpdating,
        availableItens: {
            data: availableItensQuery,
            isLoading: isLoadingAvailableItensQuery
        }
    } = usePurchaseRecommendation(selectedStoreId, supplierId, { shouldFetchPurchaseRecommendationData, shouldFetchAvailableItens });

    const { 
        query: { 
            data: ordersData, 
        },
        isLoadingOrders,
        isFetchingOrders,
        orderItemsData: {
            data: orderItemsData,
            isLoadingOrderItems,
        },
        refetchOrderItems,
        patchOrders,
        
        createOrder
    } = useOrders(selectedStoreId, selectedOrderId, { shouldFetchOrdersData });
    const isPatchingOrder = patchOrders?.isUpdating;

    const { userCtx, isLoadingStores } = useUserCtx();

    /** Dados obtidos através do model de recomendação de compras. 
     * Aqui constam operações como derivação de dados, e 
     * estabilizações para evitar renderizações excessivas na UI.
     */
    const supplierProductRelations = apiData?.supplierProductRelations;
    const allStoreSuppliers = useMemo(() => 
        apiData?.allStoreSuppliers || []
    , [apiData?.allStoreSuppliers])
    const totalProducts = apiData?.totalProducts;
    const productIds = apiData?.productIdsToUse || [];
    const productsWithoutSupplierCount = apiData?.productsWithoutSupplierCount;
    const purchaseRecommendationDataOnly = apiData?.purchaseRecommendation;

    const stabilizedSelectedSubgroups = useMemo(() => 
        Array.isArray(apiData?.selectedSubgroups) ? apiData.selectedSubgroups : [], 
        [apiData?.selectedSubgroups]
    );

    const stabilizedSelectedProducts = useMemo(() => 
        Array.isArray(apiData?.selectedProducts) ? apiData.selectedProducts : [], 
        [apiData?.selectedProducts]
    );

    /** Dados de origem dupla gerenciados pela VM
     * As informações sobre prefixos disponíveis e informações de lojas podem vir
     * tanto do model de recomendação de compras quanto de pedidos (tela: histórico de pedidos)
     */
    const tableInfo = apiData?.storeInformation || ordersData?.storeInfo?.store;
    const prefixes = apiData?.allPrefixesAvailable || ordersData?.allPrefixesAvailable;

    /** Dados derivados de user ctx  */
    const allStoresNames = userCtx?.data?.allStores;
    const allRegionsNames = userCtx?.data?.allRegions;

    /** Dados para exibição na tabela de recomendação de compras. Atualmente possui como filtros:
     * a) Subgruo;
     * b) Nome do produto;
     * c) Identificador único do produto (productId);
     * d) Status do produto; essa é uma camda extra de redundância, pois os dados já vem filtrados pela API;
     * e) Produtos elegíveis e inelegíveis a depender do estado do toggle.
     * 
     * O filtro para dados para histórico de pedidos é mais simples, e depende da existência
     * dos dados retornados pela API, ou de um orderId válido.
     * 
     * Consta nessa seção a origem das colunas para a tabela de recomendação de compras e para a tela
     * de histórico de pedidos. Cada qual separada por comentários de linha única.
     */
    // Tabela de recomendação de compras
    const filteredData = useMemo(() => {
        if (!apiData?.purchaseRecommendation) return [];

        return apiData.purchaseRecommendation.filter(item => {

            const matchesSubgroup = stabilizedSelectedSubgroups.length === 0 || stabilizedSelectedSubgroups.includes(item.productSubgroupName);
            const matchesProduct = stabilizedSelectedProducts.length === 0 || stabilizedSelectedProducts.includes(item.productName);
            const matchesProductId = !selectedProductIds?.length || selectedProductIds.includes(item.productId);
            const matchesPurchaseStatus = [ALLOWED_PURCHASE_STATUS.SUGGESTED, ALLOWED_PURCHASE_STATUS.EDITED].includes(item.purchaseStatus);
            const matchesEligibility = showNonEligibleProducts || isEligibleForPurchase(item);

            return (
                matchesSubgroup &&
                matchesProduct &&
                matchesProductId &&
                matchesPurchaseStatus &&
                matchesEligibility
            );
        });

    }, [
        selectedProductIds,
        apiData?.purchaseRecommendation,
        stabilizedSelectedSubgroups,
        stabilizedSelectedProducts,
        showNonEligibleProducts
    ]);  

    const columns = useMemo(() => [
        {
        id: "data_description",
        header: <Header ctx={{ title: ["Descrição do Produto"], tooltip: "Dados do produto" }} />,
        columns: [
            {
            accessorKey: COLUMNS_IDENTIFIERS.SUPPLIERS,
            id: COLUMNS_IDENTIFIERS.SUPPLIERS,
            header: ({ column }) => 
                <SubHeader 
                ctx={{
                    column: column,
                    title: COLUMNS_LABELS.SUPPLIERS,
                    tooltip: COLUMNS_DESCRIPTIONS.SUPPLIERS
                }}
                uiState={{ sorting: sorting, showSortButton: true }}/>,
            cell: (props) => (
                <SuppliersCell
                {...props}
                showInputs={showInputs}
                inputs={inputs}
                setInputs={setInputs}
                suppliersAndProductRelations={supplierProductRelations}
                suppliersList={allStoreSuppliers}/>        
            ),
            enableSorting: true,
            enableHiding: true,
            sortingFn: (rowA, rowB) => {
                const normalize = (row) =>
                row.original.suppliers?.[0]?.supplierName?.trim().normalize("NFC") ?? DEFAULT_VALUES.NOT_APPLY;
                return normalize(rowA).localeCompare(normalize(rowB), "pt-BR");
            },            
            },    
            {
            accessorKey: COLUMNS_IDENTIFIERS.PRODUCT_SUBGROUP_NAME,
            id: COLUMNS_IDENTIFIERS.PRODUCT_SUBGROUP_NAME,
            header: ({ column }) => 
                <SubHeader 
                ctx={{
                    column: column,
                    title: COLUMNS_LABELS.SUBGROUP,
                    tooltip: COLUMNS_DESCRIPTIONS.SUBGROUP
                }}
                uiState={{ sorting: sorting, showSortButton: true }}
                />,
            cell: ({ row }) => {
                    const originalValue = row.original.productSubgroupName;
                    const formattedValue = textFormatter(originalValue, { maxLength: 18 });
                    
                    return (
                    <CellValue 
                        value={formattedValue}
                        originalValue={originalValue}
                        isTextCell={true}
                        padding="0.4rem"
                        className="only-to-export-no-styles-added-subgroup"
                    />
                    );
                },
            enableSorting: true,
            enableHiding: true,
            },      
            {
            accessorKey: COLUMNS_IDENTIFIERS.PRODUCT_NAME,
            id: COLUMNS_IDENTIFIERS.PRODUCT_NAME,
            header: ({ column }) => 
                <SubHeader 
                ctx={{
                    column: column,
                    title: COLUMNS_LABELS.PRODUCT,
                    tooltip: COLUMNS_DESCRIPTIONS.PRODUCT
                }}
                uiState={{ sorting: sorting, showSortButton: true }}/>,
            cell: ({ row }) => {
                    const originalValue = row.original.productName;
                    const formattedValue = textFormatter(originalValue, { maxLength: 50 });
                    
                    return (
                    <CellValue 
                        value={formattedValue}
                        originalValue={originalValue}
                        isTextCell={true}
                        padding="0.4rem"
                        className="only-to-export-no-styles-added-subgroup"
                    />
                    );
                },        
            enableSorting: true,
            enableHiding: true,
            },
        ]
        },
        // dados para compra
        {
        id: "data_for_purchase",
        header: () => (
        <div style={{ display: 'flex', alignItems: "self-start" }}>
            <Header ctx={{ title: ["Dados Utilizados para Compra"], tooltip: "Dados para compra" }} />
            <IconButtonWithModal
            icon="info"
            style={{ marginTop: '-4px' }}
            modalHeading="Dados Utilizados para Compra"
            renderModalContent={({ closeModal }) => (
                <InformationForTheUser
                actions={{ closeModal }}
                ctx={{
                    intro: "A recomendação da compra considera estes 4 dados. Por isso a importância de todos estarem preenchidos e corretos.",
                    items: [
                    { label: "Estoque em Loja:", text: "se o Estoque em Loja não estiver correto, reporte à sua liderança." },
                    { label: "Venda Diária:", text: "é a média da venda dos últimos 15 dias." },
                    { label: "Estoque Ideal:", text: "quantidade, em unidades, idealmente para ter na loja. O ideal é refletir a quantidade que evita \"buracos\" na loja e o tempo de reposição do fornecedor." },
                    { label: "Tempo de Entrega:", text: "tempo que o fornecedor leva, em dias, para entregar um pedido de compra." },
                    { label: "Obs.:", text: "as mudanças realizadas serão utilizadas no dia seguinte, na próxima recomendação de compra que o sistema fará. Por isso não irá refletir de imediato.", italic: true },
                    ]
                }}
                />
            )}
            useStyledModal
            />
        </div>
        ),
        columns: [
            {
                id: COLUMNS_IDENTIFIERS.PHYSICAL_STOCK,
                header: <Header ctx={{ title: ["Estoque em Loja"], tooltip: COLUMNS_DESCRIPTIONS.PHYSICAL_STOCK }} />,
                columns: [
                    {
                        id: COLUMNS_IDENTIFIERS.PHYSICAL_STOCK,
                        accessorKey: COLUMNS_IDENTIFIERS.PHYSICAL_STOCK,                        
                        header: ({ column }) => 
                            <SubHeader 
                            ctx={{
                                column: column,
                                //title: COLUMNS_LABELS.PHYSICAL_STOCK,
                                tooltip: COLUMNS_DESCRIPTIONS.PHYSICAL_STOCK
                            }}
                            uiState={{ sorting: sorting, showSortButton: true }}
                            />,
                        cell: ({ getValue }) => <CellValue value={getValue()} />,
                        enableSorting: true,
                        enableHiding: true,
                    }
                ]
            },
            {
                id: COLUMNS_IDENTIFIERS.AVERAGE_SALES,
                header: <Header ctx={{ title: [t("sales"), t("daily")], tooltip: COLUMNS_DESCRIPTIONS.AVERAGE_SALES }} />,
                columns: [
                    {
                    accessorKey: COLUMNS_IDENTIFIERS.AVERAGE_SALES,
                    id: COLUMNS_IDENTIFIERS.AVERAGE_SALES,
                    header: ({ column }) => 
                        <SubHeader 
                        ctx={{
                            column: column,
                            title: "(Méd. 15d)",
                            tooltip: "Venda diária média do produto nos últimos 15 dias"
                        }}
                        uiState={{ sorting: sorting, showSortButton: true }}/>,
                cell: ({ getValue }) => <CellValue value={getValue()} />,
                    enableSorting: true,
                    enableHiding: true,
                    },
                ],
                enableSorting: true,
                enableHiding: true,                
            },
            {
                id: COLUMNS_IDENTIFIERS.MIN_STOCK,
                accessorKey: COLUMNS_IDENTIFIERS.MIN_STOCK,
                header: <Header ctx={{ title: [t("minimumInventory_pt1"), t("minimumInventory_pt2")], tooltip: t("productMinimumInvetory") }} />,
                columns: [{
                    id: COLUMNS_IDENTIFIERS.MIN_STOCK,
                    accessorKey: COLUMNS_IDENTIFIERS.MIN_STOCK,
                    header: ({ column }) => 
                    <SubHeader 
                        ctx={{
                        column: column,
                        title: "(UN)",
                        tooltip: t("productMinimumQty")
                        }}
                        uiState={{ sorting: sorting}}/>,
                    cell: (props) => (
                    <MinStockCell
                        {...props}
                        showInputs={showInputs}
                        inputs={inputs}
                        suppliersAndProductRelations={supplierProductRelations}
                        setInputs={setInputs}/>
                    ),        
                }],
                enableSorting: false,
                enableHiding: true,                
            },
            {
                id: COLUMNS_IDENTIFIERS.LEAD_TIME,
                accessorKey: COLUMNS_IDENTIFIERS.LEAD_TIME,      
                header: <Header ctx={{ title: [t("leadTime_pt1"), t("leadTime_pt2")], tooltip: t("productLeadTimeStore")}} />,
                columns: [{
                    id: COLUMNS_IDENTIFIERS.LEAD_TIME,
                    accessorKey: COLUMNS_IDENTIFIERS.LEAD_TIME,
                    header: ({ column }) =>   
                    <SubHeader 
                        ctx={{
                        column: column,
                        title: t("daysTitle"),
                        tooltip: t("productLeadTime")
                        }}
                        uiState={{ sorting: sorting }}/>,
                    cell: (props) => (
                    <LeadTimeCell
                        {...props}
                        suppliersAndProductRelations={supplierProductRelations}
                        showInputs={showInputs}
                        inputs={inputs}
                        setInputs={setInputs}/>
                    ),
                }],
                enableSorting: false,
                enableHiding: true,
            }
        ]
        },
        // processo de compra
        {
        id: "purchase_proc",
        header: () => (
        <div style={{ display: 'flex', alignItems: "self-start" }}>
            <Header ctx={{ title: ["Processo de Compra"], tooltip: "Processo da compra" }} />
            <IconButtonWithModal
            icon="info"
            style={{ marginTop: '-4px' }}
            modalHeading="Processo de Compra"
            renderModalContent={({ closeModal }) => (
                <InformationForTheUser
                actions={{ closeModal }}
                ctx={{
                    intro: "O Sistema irá sugerir a compra baseado em 3 Passos, de acordo com as 3 colunas que estão no Processo de Compra:",
                    items: [
                    { label: "1 - Unidades a Serem Compradas:", text: "esta coluna indica qual deve ser a compra, em unidades, se você deseja ter estoque para os próximos 7 dias, 14 dias ou 21 dias." },
                    { label: "2 - Unidade de Medida:", text: "nem todos os produtos podem ser comprados em unidades. Alguns precisam ser comprados em fardos, caixas, etc. Esta coluna indica qual é essa unidade de medida e qual o conversor em unidades dela. Exemplo: Um fardo de refrigerante de 6 garrafas deve ter Sigla=Fardo e Conversor=6." },
                    { label: "3 - Compra Final:", text: "é a compra da Unidade de Medida deste produto. Exemplo: se eu preciso comprar 18 refrigerantes e o fornecedor só vende em Fardos de 6 garrafas, a Compra Final será igual a 3 (ou seja, 3 Fardos, que resultará em 18 unidades)." },
                    ]
                }}
                />
            )}
            useStyledModal
            />
        </div>
        ),      
        columns: [
        {
        id: COLUMNS_IDENTIFIERS.PURCHASE_REC_7,
        header: <Header ctx={{ title: ["1 - Unidades a Serem Compradas"], tooltip: t("projectedSalesProduct") }} />,
        columns: [
            {
            accessorKey: COLUMNS_IDENTIFIERS.PURCHASE_REC_7,
            id: COLUMNS_IDENTIFIERS.PURCHASE_REC_7,
            header: ({ column }) => 
                <SubHeader 
                actions={{ setInputs: setInputs }}
                ctx={{
                    column: column,
                    title: "(7d)",
                    tooltip: t("projectedSalesNext7Days"),
                    suppliersAndProductRelations: supplierProductRelations,
                    filteredData: filteredData,
                }}
                uiState={{ 
                    sorting: sorting,
                    inputs: inputs,
                    showSortButton: true,
                    updateBacthCondition: !!(appliedFiltersCount && showInputs)
                    }}/>,
            cell: (props) => (
                <ProjectedSalesCell 
                {...props} 
                showInputs={showInputs}
                inputs={inputs}
                suppliersAndProductRelations={supplierProductRelations}
                setInputs={setInputs}
                columnId={COLUMNS_IDENTIFIERS.PURCHASE_REC_7} />
            ),
            enableSorting: true,
            enableHiding: true,
            },
            {
            accessorKey: COLUMNS_IDENTIFIERS.PURCHASE_REC_14,
            id: COLUMNS_IDENTIFIERS.PURCHASE_REC_14,
            header: ({ column }) => 
                <SubHeader
                actions={{ setInputs: setInputs }}
                ctx={{
                    column: column,
                    title: "(14d)",
                    tooltip: t("projectedSalesNext14Days"),
                    suppliersAndProductRelations: supplierProductRelations,
                    filteredData: filteredData,
                }}
                uiState={{ 
                    sorting: sorting,
                    inputs: inputs,
                    showSortButton: true,
                    updateBacthCondition: !!(appliedFiltersCount && showInputs)
                    }}/>,
            cell: (props) => (
                <ProjectedSalesCell 
                {...props} 
                showInputs={showInputs}
                inputs={inputs}
                suppliersAndProductRelations={supplierProductRelations}
                setInputs={setInputs}
                columnId={COLUMNS_IDENTIFIERS.PURCHASE_REC_14} />
            ),
            enableSorting: true,
            enableHiding: true,
            },
            {
            accessorKey: COLUMNS_IDENTIFIERS.PURCHASE_REC_21,
            id: COLUMNS_IDENTIFIERS.PURCHASE_REC_21,
            header: ({ column }) => 
                <SubHeader
                actions={{ setInputs: setInputs }}
                ctx={{
                    column: column,
                    title: "(21d)",
                    tooltip: t("projectedSalesNext21Days"),
                    suppliersAndProductRelations: supplierProductRelations,
                    filteredData: filteredData,
                }}
                uiState={{ 
                    sorting: sorting,
                    inputs: inputs,
                    showSortButton: true,
                    updateBacthCondition: !!(appliedFiltersCount && showInputs)
                    }}/>,
            cell: (props) => (
                <ProjectedSalesCell 
                {...props} 
                showInputs={showInputs}
                inputs={inputs}
                suppliersAndProductRelations={supplierProductRelations}
                setInputs={setInputs}
                columnId={COLUMNS_IDENTIFIERS.PURCHASE_REC_21} />
            ),
            enableSorting: true,
            enableHiding: true,
            },
        ],
        },
        // unidade medida
        {
        id: COLUMNS_IDENTIFIERS.PREFIX_NAME,
        accessorKey: COLUMNS_IDENTIFIERS.PREFIX_NAME,
        header: <Header ctx={{ title: ["2 - Unidade de Medida"], tooltip: t("productUnitOfMeasure") }} />,   
        columns: [
            {
            id: COLUMNS_IDENTIFIERS.PREFIX_NAME,
            accessorKey: COLUMNS_IDENTIFIERS.PREFIX_NAME,
            header: ({ column }) => 
            <SubHeader 
                ctx={{
                column: column,
                title: t("code"),
                tooltip: t("productPurchaseUnit"),
                filteredData: filteredData
                }}
                uiState={{ sorting: sorting, showSortButton: true }}/>,
            cell: (props) => {
                const role = vmSettings?.ctx?.userRole;

                const isPrivilegedRole = role === USER_ROLES.REGIONAL_GAS_STATION || (role === USER_ROLES.TENANT_ADMIN || role === USER_ROLES.ADMIN);

                if (isPrivilegedRole) {
                    return (
                        <PrefixNameCell
                            {...props}
                            showInputs={showInputs}
                            inputs={inputs}
                            setInputs={setInputs}
                            prefixes={prefixes}
                            suppliersAndProductRelations={supplierProductRelations}
                        />
                    );
                }

                return (
                    <PrefixNameCell
                        {...props}
                        prefixes={prefixes}              
                    />
                );
            },

            enableSorting: true,
            enableHiding: true,            
            },
                {
                id: COLUMNS_IDENTIFIERS.PACK_TO_UNIT_RATIO,
                accessorKey: COLUMNS_IDENTIFIERS.PACK_TO_UNIT_RATIO,
                header: ({ column }) => 
                <SubHeader 
                    ctx={{
                    column: column,
                    title: t("conversionFactor"),
                    tooltip: t("conversionFactorDescription")
                    }}
                    uiState={{ sorting: sorting, showSortButton: true }}/>,

                cell: (props) => {
                    const role = vmSettings?.ctx?.userRole;

                    const isPrivilegedRole =
                        role === USER_ROLES.REGIONAL_GAS_STATION ||
                        (role === USER_ROLES.TENANT_ADMIN || role === USER_ROLES.ADMIN);

                    if (isPrivilegedRole) {
                        return (
                            <PackToUnitRatioCell
                                {...props}
                                showInputs={showInputs}
                                inputs={inputs}
                                setInputs={setInputs}
                                suppliersAndProductRelations={supplierProductRelations}
                                saveLoading={isUpdating}
                            />
                        );
                    }

                    return (
                        <PackToUnitRatioCell
                            {...props}
                        />
                    );
                },      
                enableSorting: true,
                enableHiding: true,              
                },
        ],
        },
            // compra final
            {
            accessorKey: COLUMNS_IDENTIFIERS.EFFECTIVE_PURCHASE,
            id: COLUMNS_IDENTIFIERS.EFFECTIVE_PURCHASE,
            header: <Header 
                ctx={{ title: ["3 - Compra Final"], tooltip: t("effectivePurchaseProductValue") }} 
                uiState={{ isHeaderTitleHighlighted: true }} />,
            columns: [
                {
                accessorKey: COLUMNS_IDENTIFIERS.EFFECTIVE_PURCHASE,
                id: COLUMNS_IDENTIFIERS.EFFECTIVE_PURCHASE,
                header: ({ column }) => 
                    <SubHeader 
                    ctx={{
                        column: column,
                        title: t("unitOfMeasure_title"),
                        tooltip: t("totalQtyToBeBought")
                    }}
                    uiState={{ sorting: sorting }}/>,
                cell: (props) => (
                    <EffectivePurchaseCell
                    {...props}
                    showInputs={showInputs}
                    inputs={inputs}
                    suppliersAndProductRelations={supplierProductRelations}
                    setInputs={setInputs}/>
                ),
                enableSorting: false,
                enableHiding: true,
                },
            ],
            enableSorting: true,
            enableHiding: true,      
            },
        ]
        }
    ], [showInputs, inputs, sorting, prefixes, filteredData, appliedFiltersCount, supplierProductRelations, allStoreSuppliers, isUpdating, t, vmSettings?.ctx?.userRole]);

    // Histórico de pedidos
    const filteredOrders = useMemo(() => {
        if (!ordersData?.orders || !selectedStoreId) return [];
        return ordersData.orders;
    }, [ordersData?.orders, selectedStoreId]);  

    const getPurchaseHistoryColumns = () => [
        {
            id: "details",
            header: <Header
                ctx={{
                    title: [COLUMNS_LABELS.DETAILS],
                    tooltip: "Veja detalhes do pedido"
                }}
                uiState={{
                    headerFontSize: "0.8rem"
                }}
            />,
            cell: ({ row }) => (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                    }}
                    onClick={() => handleOpenModal(row.original.orderId)}
                >
                    <SearchIcon size={40} color="#FF6600" />
                </div>
            ),
        },
        {
            accessorKey: COLUMNS_IDENTIFIERS.SUPPLIER_NAME,
            id: COLUMNS_IDENTIFIERS.SUPPLIER_NAME,
            header: () => (
                <Header
                    ctx={{ title: [COLUMNS_LABELS.SUPPLIER], tooltip: "Fornecedor do pedido" }}
                    uiState={{ headerFontSize: "0.8rem" }}
                />
            ),
            cell: ({ row }) => {
                    const originalValue = row.original.supplierName;
                    const formattedValue = textFormatter(originalValue, { textTransform: 'uppercase' });
                    
                    return (
                    <CellValue 
                        value={formattedValue}
                        originalValue={originalValue}
                        isTextCell={true}
                        padding="0.4rem"
                    />
                    );
                },         
            enableSorting: true,
        },
        {
            accessorKey: COLUMNS_IDENTIFIERS.ORDER_ID,
            id: COLUMNS_IDENTIFIERS.ORDER_ID,
            header: () => (
                <Header
                    ctx={{ title: [COLUMNS_LABELS.ORDER_ID], tooltip: "Identificador do pedido" }}
                    uiState={{ headerFontSize: "0.8rem" }}
                />
            ),          
            cell: ({ row }) => {
                const originalValue = row.original.orderId;
                
                return (
                <CellValue 
                    value={originalValue}
                    originalValue={originalValue}
                    isTextCell={false}
                    padding="0.4rem"
                    align={'center'}
                />
                );
            },  
            enableSorting: true,
        },        
        {
            accessorKey: COLUMNS_IDENTIFIERS.UI_PURCHASE_DATE,
            id: COLUMNS_IDENTIFIERS.UI_PURCHASE_DATE,
            header: () => (
                <Header
                    ctx={{ title: [COLUMNS_LABELS.ORDER_DATE], tooltip: "Data de realização do pedido" }}
                    uiState={{ headerFontSize: "0.8rem" }}
                />
            ),               
            enableSorting: true,
            cell: (info) => {
            const rawValue = info.getValue() || info.row?.original?.orderDate;
            const date = new Date(rawValue);
            const formatted = date.toLocaleString('pt-BR', {
                timeZone: 'America/Sao_Paulo',
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            });

            return (
                <CellValue
                value={formatted}
                originalValue={formatted}
                isTextCell={true}
                align="center"
                />
            );
            },
        },
        {
            accessorKey: COLUMNS_IDENTIFIERS.USERNAME,
            id: COLUMNS_IDENTIFIERS.USERNAME,
            header: () => (
                <Header
                    ctx={{ title: [COLUMNS_LABELS.USERNAME], tooltip: "Usuário que realizou o pedido" }}
                    uiState={{ headerFontSize: "0.8rem" }}
                />
            ),            
            enableSorting: true,
            cell: (info) => {
            const username = info.getValue();
            const fallbackTenantName = info.row.original.name;

            return (
                <CellValue
                value={username || fallbackTenantName}
                originalValue={username || fallbackTenantName}
                isTextCell={false}
                align="center"
                />
            );
            },
        },
        {
        accessorKey: COLUMNS_IDENTIFIERS.ORDER_STATUS,
        id: COLUMNS_IDENTIFIERS.ORDER_STATUS,
        header: () => (
            <Header
                ctx={{ title: [COLUMNS_LABELS.ORDER_STATUS], tooltip: "Status do pedido" }}
                uiState={{ headerFontSize: "0.8rem" }}
            />
        ),          
        enableSorting: true,
        cell: (info) => {
            const mapOrderStatus = {
                [ORDER_STATUS.CANCELED]: 'Cancelado',
                [ORDER_STATUS.REALIZED]: 'Realizado',
                [ORDER_STATUS.CONCLUDED]: 'Concluído'
            };

            const mapColorsToOrderStatus = {
                [ORDER_STATUS.CANCELED]: '#ef4444',
                [ORDER_STATUS.REALIZED]: '#f59e0b',
                [ORDER_STATUS.CONCLUDED]: '#10b981'
            }
            const value = info.getValue();
            const displayValue = mapOrderStatus[value] || value;
                
            const prefixStyle = {
                display: 'inline-block',
                textAlign: 'center',
                fontWeight: '700',
                color: '#fff',
                backgroundColor: mapColorsToOrderStatus[value],
                padding: '0.25rem 0.6rem',
                borderRadius: '9999px',
                boxShadow: `0 2px 8px #00000033`,
                minWidth: '60px'
            };

                return <span style={prefixStyle}>{displayValue}</span>;
            },
        },
        {
        id: COLUMNS_LABELS.MODIFY,
        header: () => (
            <Header
                ctx={{ title: [COLUMNS_LABELS.MODIFY], tooltip: "Opções de edição do pedido" }}
                uiState={{ headerFontSize: "0.8rem" }}
            />
        ),           
        cell: ({ row }) => {

            const loggedUserId = vmSettings?.ctx?.userId;
            const orderUserId  = row?.original?.userId;

            const role = vmSettings?.ctx?.userRole;

            const isOwner = loggedUserId === orderUserId;
            const isPrivilegedRole =
            role === USER_ROLES.REGIONAL_GAS_STATION ||
            role === USER_ROLES.TENANT_ADMIN;

            const canEdit = isOwner || isPrivilegedRole;

            if (!canEdit) {
                return (
                    <div
                    style={{
                        display: "flex",
                        gap: "0.5rem",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "40px",
                    }}
                    />
                );
            };

            const allowCancelBottom = row.original.orderStatus !== ORDER_STATUS.REALIZED;

            return (
            <div
                style={{
                display: "flex",
                gap: "0.5rem",
                justifyContent: "center",
                alignItems: "center",
                }}
            >
{/*                 <Button
                    onClick={() => handleOpenModal(row.original.orderId, true)}
                    label={"Detalhes"}
                    icon="layout"
                    type={"filled"}
                    size="sm"
                /> */}
                <Button
                    onClick={() => handleOpenCancelModal(row.original.orderId)}
                    label={t("cancelOrder")}
                    icon="layout"
                    type={"filled"}
                    size="sm"
                    disabled={allowCancelBottom}
                />
            </div>
            );
        },
        }
    ];

    const orderDetailsModalColumns = useMemo(() => {
        const baseColumns = [
        {
            header: COLUMNS_LABELS.PRODUCT,
            accessorKey: COLUMNS_IDENTIFIERS.PRODUCT_NAME,
            cell: info => (
            <div style={{ display: 'flex', gap: '1rem', fontWeight: '500', color: '#374151' }}>
                {info.getValue()}
                {info.row.original.isItemRecentlyAdded && (
                    <span style={{
                    padding: "0.2rem 0.4rem",
                    backgroundColor: "#FFF5EC",
                    color: "#e75d0d",
                    fontSize: "0.6rem",
                    fontWeight: "700",
                    borderRadius: "9999px",
                    fontFamily: "Lato, sans-serif",
                    }}>
                    novo
                    </span>
                )}                
            </div>
            ),
            enableSorting: true
        },
        {
            header: COLUMNS_LABELS.EFFECTIVE_PURCHASE,
            accessorKey: COLUMNS_IDENTIFIERS.EFFECTIVE_PURCHASE,
            id: COLUMNS_IDENTIFIERS.EFFECTIVE_PURCHASE,
            cell: (props) => (
            <EffectivePurchaseCell
                {...props}
                showInputs={showInputs}
                inputs={inputs}
                setInputs={setInputs}
                isOrderHystoryPage={true}
                saveLoading={isUpdating}/>
            ),
            enableSorting: true
        },
        {
            header: COLUMNS_LABELS.PACK_TO_UNIT_RATIO,
            accessorKey: COLUMNS_IDENTIFIERS.PACK_TO_UNIT_RATIO,
            id: COLUMNS_IDENTIFIERS.PACK_TO_UNIT_RATIO,
            cell: (props) => (
            <PackToUnitRatioCell
                {...props}
                isOrderHystoryPage={true}
                setInputs={setInputs}
                saveLoading={isUpdating}/> 
            ),
            enableSorting: true
        },
        {
            header: COLUMNS_LABELS.UNIT_OF_MEASURE,
            accessorKey: COLUMNS_IDENTIFIERS.PREFIX_NAME,
            id: COLUMNS_IDENTIFIERS.PREFIX_NAME,
            cell: (props) => (
            <PrefixNameCell
                {...props}
                isOrderHystoryPage={true}
                setInputs={setInputs}
                prefixes={prefixes}
                saveLoading={isUpdating}/>        
            ),
            enableSorting: true
        }
        ];

        const selectionColumn = {
            header: t("exclude"),
            id: "select-product",
            cell: ({ row }) => {
                if (row.original.isItemRecentlyAdded) {
                return (
                    <button
                    onClick={() => setLocalOrderItems(prev =>
                        prev.filter(item => item.productId !== row.original.productId)
                    )}
                    style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#e75d0d",
                        fontSize: "1.1rem",
                        padding: "0 8px",
                    }}
                    >
                    ✕
                    </button>
                );
                }
                return (
                <SelectCell
                    ctx={{ data: row.original }}
                    actions={{ setCheckedProducts }}
                    uiState={{ showInputs, checkedProducts }}
                />
                );
            },
            enableSorting: false
        };

        return showInputs ? [selectionColumn, ...baseColumns] : baseColumns;
    }, [showInputs, inputs, isUpdating, checkedProducts, prefixes, t]);

    /** Dados derivados da API de pedidos para filtro de itens de pedidos,
     * e demais informações necessárias para renderização dos dados na UI
     */
    const orderInfo = useMemo(() => {
        if (!Array.isArray(ordersData?.orders)) return {};
        return ordersData.orders.find(o => o.orderId === selectedOrderId) || {};
    }, [ordersData, selectedOrderId]);

    const orderItemsToUse = useMemo(() => {
    if (!orderItemsData || !Array.isArray(orderItemsData)) return [];

    const isOrderFullyCanceled = orderItemsData.every(
        item => item.orderItemStatus === ORDER_ITEM_STATUS.CANCELED
    );

    if (isOrderFullyCanceled) return orderItemsData;

    return orderItemsData.filter(
        item => item.purchaseRecommendationId !== null
        && item.uiPurchaseDate !== null
        && item.orderItemStatus !== ORDER_ITEM_STATUS.INACTIVE
    );
    }, [orderItemsData]);

    /** Ações gerenciadas pela VM 
     * Aqui constam ações que não dizem respeito a gerenciamento de estado e 
     * que podem ser utilizadas tanto na tabela de recomendação de compras
     * quanto na UI de histórico de pedidos. Cada qual será indicada
     * via comentário de linha única
     */

    // tabela de recomendação de compras
    const handleSave = async (inputData) => {
        try {
            return await updateRecommendation(inputData);
        } catch (error) {
            setShowUpdateRecommendationError(true);
        }
    };

    const onInputsSave = async () => {
    try {
        const result = await updateRecommendation(inputs);

        if (!result) return;

        const isPartial = result?.partial === true;
        const updated = isPartial ? result.updated : result;
        const blocked = isPartial ? result.blocked : [];

        setUpdatedProductsList(updated);
        setBlockedProductsList(blocked);
        setShowSuccessModal(true);
        setShowInputs(false);
        setInputs([]);

    } catch (error) {
        setShowSuccessModal(false);
        setShowUpdateRecommendationError(true);
        setShowInputs(false);
        setInputs([]);
    } finally {
        setInputs([]);
        setShowInputs(false);
    }
    };

    const handleToggleEligibleProducts = useCallback((checked) => {
        setShowNonEligibleProducts(checked);
    }, []);

    // histórico de pedidos
    const handleOpenModal = (orderId) => {
        setSelectedOrderId(orderId);
        setShowModal(true);
    };

    const handleOpenCancelModal = (orderId) => {
        setSelectedOrderId(orderId);
        setShowCancelModalOrderRollback(true);
    };

    // efeitos para sync
    // remove os itens do pedido atual
    useEffect(() => {
        setLocalOrderItems([]);
    }, [selectedOrderId])

    // insere os novos itens
    useEffect(() => {
        if (orderItemsToUse.length > 0) setLocalOrderItems(orderItemsToUse);
    }, [selectedOrderId, orderItemsToUse]);

    return {
        actions: {
            createOrder,
            patchOrders,

            handleSave,            

            setFreezeColumns: setFreezeColumnsAction,
            setInputs: setInputsAction,
            setSelectedSupplierFromFilter,            
            setShowColumnFilters: setShowColumnFiltersAction,            
            setShowCancelModal,
            setShowInputs,

            onInputsSave,
            setIsPatchingOrderInParent,
            setOpenOrderConfirmation,
            setSelectedProductIds: setSelectedProductIds,
            updateRecommendation,

            setBlockedProductsList: setBlockedProductsList,

            setShowCancelModalOrderRollback,
            setShowModal,
            setShowSuccessModal,
            refetchOrderItems,
            setUpdatedProductsList,
            setAppliedFiltersCount: setAppliedFiltersCountAction,
            setShowFilters,
            setCheckedProducts,
            handleOpenModal,
            handleOpenCancelModal,
            setSorting,
            setShowUpdateRecommendationError,

            handleToggleEligibleProducts,

            setLocalOrderItems
        },
        ctx: {
            filteredData,
            selectedProductIds,
            totalProducts,
            suppliersList: allStoreSuppliers,
            ordersData: ordersData?.orders,
            productsWithoutSupplierCount,
            orderItemsData,
            tableInfo,
            updatedProductsList,
            blockedProductsList: blockedProductsList,
            storeId: selectedStoreId,
            orderDetailsModalColumns,
            getPurchaseHistoryColumns,
            prefixes,
            selectedOrderId,
            selectedSupplierFromFilter,
            productIds,
            columnsSalesTable: columns,
            filteredOrders,
            supplierProductRelations,
            allStoresNames,
            allRegionsNames,
            purchaseRecommendationDataOnly,
            orderInfo,
            orderItemsToUse: localOrderItems,
            availableItens: { 
                availableItensFetched: availableItensQuery 
            }
        },
        uiState: {
            freezeColumns,
            showColumnFilters,
            inputs,
            showInputs,
            appliedFiltersCount,
            openOrderConfirmation,
            sorting: sorting,
            isUpdating,
            showCancelModalOrderRollback,
            checkedProducts,
            isLoadingOrderItems,
            isLoadingOrders,
            isFetchingOrders,
            isLoading: isLoading,
            showFilters,
            showCancelModal,
            showSuccessModal,
            isPatchingOrder,
            isPatchingOrderInParent,
            purchaseOrderEligibility: {
                withUserInputs: getPurchaseOrderEligibility(
                    ELIGIBLE_ORDERS_STRATEGY_VALUES.WITH_USER_INPUTS, { filteredData, inputs }
                ),
                withOriginalValues: getPurchaseOrderEligibility(
                    ELIGIBLE_ORDERS_STRATEGY_VALUES.API_DATA, { filteredData }
                ),
                eligibleOrders: getPurchaseOrderEligibility(
                    ELIGIBLE_ORDERS_STRATEGY_VALUES.ELIGIBLE_ORDERS, { 
                        purchaseRecommendation: purchaseRecommendationDataOnly, 
                        supplierProductRelations
                    }
                )
            },
            isLoadingStores,
            showModal,
            errorOfPurchaseRecommendationUpdate: updateError,
            showUpdateRecommendationError,
            showNonEligibleProducts,
            availableItens: {
                isLoadingFetchAvailableItens: isLoadingAvailableItensQuery
            }
        }
    }
};